import { useCallback, useRef, useState } from "react";
import styled from "styled-components";
import { colors } from "constants/colors";
import { StyledText } from "styles/Text";
import { useAlertStore } from "stores/alertStore";
import { toast } from "util/common";
import {
  useDeleteCommonReply,
  useGetCommonReply,
  usePostCommonReply,
} from "api/queries";
import { ApiPostCommonReplyParams } from "types/Common";
import { CommonReplyModel } from "models/commonReplyModel";
import RoundRectangleButton from "components/atoms/RoundRectangleButton";
import PagingNav from "components/atoms/PagingNav";

interface Props {
  svcId: number;
  pageSize?: number;
}

const Comment = ({ svcId, pageSize }: Props) => {
  const nickNameRef = useRef<HTMLInputElement>(null);
  const contentRef = useRef<HTMLTextAreaElement>(null);
  const { showAlert, showConfirmAlert } = useAlertStore();
  const [page, setPage] = useState(1);
  const { data, refetch } = useGetCommonReply(svcId, page, pageSize);
  const postMutation = usePostCommonReply();
  const deleteMutation = useDeleteCommonReply();

  const onClickPrev = () => {
    if (page > 1) {
      setPage(page - 1);
    }
  };
  const onClickNext = useCallback(() => {
    if (data?.totalCount && page < data.totalCount) {
      setPage(page + 1);
    }
  }, [page, data?.totalCount]);

  // 댓글 등록
  const replySubmit = () => {
    if (nickNameRef.current?.value && contentRef.current?.value) {
      const writeNickName = nickNameRef.current.value;
      const content = contentRef.current.value;
      if (content.length > 200) {
        return showAlert({ message: "내용은 200자까지만 입력 가능합니다." });
      }

      const params: ApiPostCommonReplyParams = {
        svcId,
        content,
        writeNickName,
      };
      postMutation.mutate(params, {
        onSuccess: () => {
          refetch();
          setPage(1);
          toast("댓글이 등록되었습니다.");
          nickNameRef.current && (nickNameRef.current.value = "");
          contentRef.current && (contentRef.current.value = "");
        },
      });
    } else {
      showAlert({
        message: !nickNameRef.current?.value
          ? "이름을 입력해 주세요."
          : "메세지를 입력해 주세요.",
      });
    }
  };

  // 댓글 삭제
  const deleteReply = (replyId: number) => {
    showConfirmAlert({
      message: "정말 삭제하시겠어요?",
      okButtonText: "예",
      okOnPress: () => deleteReplyAPI(replyId),
      cancelButtonText: "아니오",
    });
  };
  const deleteReplyAPI = (replyId: number) => {
    deleteMutation.mutate(replyId, {
      onSuccess: () => refetch(),
    });
  };

  return (
    <>
      {/* 댓글 작성 */}
      <StyledContainer>
        <StyledWriteBox>
          <div>
            <StyledText.Sub11
              as="label"
              htmlFor="commentName"
              color={colors.subBody3}
            >
              이름
            </StyledText.Sub11>
            <StyledText.Body13
              as="input"
              type="text"
              id="commentName"
              ref={nickNameRef}
              placeholder="작성자의 이름을 입력하세요."
            />
          </div>

          <div>
            <StyledText.Sub11
              as="label"
              htmlFor="commentText"
              color={colors.subBody3}
            >
              메세지
            </StyledText.Sub11>
            <StyledText.Body13
              as="textarea"
              id="commentText"
              ref={contentRef}
              placeholder="최대 200자까지 입력가능해요.
다른 사용자에게 불쾌감을 주거나 광고, 홍보 목적의 상업적인 댓글, 유해 정보 등 서비스 운영원칙에 위배되는 글의 경우 고지없이 삭제될 수 있습니다."
            ></StyledText.Body13>
          </div>
        </StyledWriteBox>

        <RoundRectangleButton
          text="메세지 등록하기"
          bgColor={colors.subBody3}
          width="100%"
          $marginTop={24}
          onClick={replySubmit}
        />
      </StyledContainer>

      {/* 댓글 리스트 */}
      {data?.items && page && data.totalCount && (
        <StyledList>
          <ul>
            {data.items.map((item: CommonReplyModel) => (
              <li key={`commandItem_${item.replyId}`}>
                <div className="comment_info">
                  <em className="txt_nickname">{item.writeNickName}</em>
                  <span>{item.createDtForSns}</span>
                  {!!item.isMine && (
                    <button
                      type="button"
                      className="btn_del_comment"
                      onClick={() => item.replyId && deleteReply(item.replyId)}
                    >
                      삭제
                    </button>
                  )}
                </div>
                {item.content}
              </li>
            ))}
          </ul>
          <PagingNav
            page={page}
            totalCount={data.totalCount}
            onClickPrev={onClickPrev}
            onClickNext={onClickNext}
          />
        </StyledList>
      )}
    </>
  );
};

export default Comment;

const StyledContainer = styled.section`
  padding: 24px 16px 48px;
`;

const StyledWriteBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  padding: 16px 12px;
  border-radius: 12px;
  border: 1px solid ${colors.lightGray2};
  background-color: ${colors.white};
  label {
    display: block;
    padding-bottom: 8px;
  }
  input,
  textarea {
    display: block;
    width: 100%;
    border: 0;
    &::placeholder {
      color: ${colors.gray};
    }
  }
  textarea {
    resize: none;
    min-height: 100px;
  }
`;

const StyledList = styled.ul`
  margin: 0 16px 48px;
  li {
    margin-top: 16px;
    padding: 16px 12px;
    background-color: ${colors.dimBg};
    border-radius: 12px;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: -0.7px;
    word-break: break-word;
  }
  .comment_info {
    margin-bottom: 16px;
    font-size: 12px;
    line-height: 18px;
    letter-spacing: -0.6px;
    color: ${colors.subBody2};
  }
  .txt_nickname {
    margin-right: 8px;
    font-weight: 500;
    color: ${colors.subBody3};
  }
  .btn_del_comment {
    float: right;
    font-size: inherit;
    line-height: inherit;
    letter-spacing: inherit;
    color: ${colors.subBody2};
  }
`;
